import {
  MutableRefObject,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { MdDragHandle } from "react-icons/md";
import MetaAreaDiv from "../MetaAreaDiv";
import { ProjectInfo } from "../../../../../APITypes/ProjectInfo.cs";
import { RxDragHandleHorizontal } from "react-icons/rx";
import { SessionPlaylistItem } from "@/types";
import TimeLineTile from "./TimeLineTile";
import { ChevronRight, Webcam } from "lucide-react";
import { cn } from "../../../../../lib/utils";
import { useDataContext } from "../../../../../app/_context";

interface TimeLineProps {
  // CORE PROPS /////////////////////////////////////////////////////////////
  meta: ProjectInfo;
  pid: string;
  hasContent: boolean;
  // MODES AND DIMENSIONS ///////////////////////////////////////////////////
  widgetMode: boolean;
  skeleton: boolean;
}

export function TimeLine({ meta, pid, hasContent, widgetMode }: TimeLineProps) {
  const {
    currentSessionIndex,
    currentPlaylist,
    isHighlightsOnlyMode,
    setCurrentSessionIndex,
    setIsBusinessCardPlaying,
    setCurrentSegment,
    setCurrentPlaybackTime,
    setWasBusinessCardShown,
    setSkipToTime,
    requestPlayWhenReady,
  } = useDataContext();
  // REFS /////////////////////////////////////////////////////////////////////////
  const timelineScrollEl = useRef<HTMLDivElement>(
    null,
  ) as MutableRefObject<HTMLDivElement>;
  const currentPlaylistItem = useRef<HTMLButtonElement>(
    null,
  ) as MutableRefObject<HTMLButtonElement>;
  const anchor = useRef(null);
  const [rolloverTimeline, setRollOverTimeline] = useState(false);
  const [scrollPosTimeline, setScrollPosTimeline] = useState(0);
  // USE EFFECTS //////////////////////////////////////////////////////////////////
  useEffect(() => {
    if (!hasContent) {
      return;
    }
    timelineScrollEl.current.addEventListener("wheel", scrollHorizontally, {
      passive: true,
    });
    return () => {
      if (timelineScrollEl.current != undefined) {
        timelineScrollEl.current.removeEventListener(
          "wheel",
          scrollHorizontally,
          undefined,
        );
      }
    };
  }, [rolloverTimeline]);
  //
  useEffect(() => {
    console.log("currentSessionIndex changed", currentSessionIndex);
    scrollToCurrentElement();
  }, [currentSessionIndex]);
  // FUNCTIONS ////////////////////////////////////////////////////////////////////
  const scrollHorizontally = (e: WheelEvent) => {
    if (!hasContent) {
      return;
    }
    const delta = Math.max(-1, Math.min(1, e.deltaY));
    timelineScrollEl.current.scrollLeft += delta * 40;

    const scrollLeft = timelineScrollEl.current.scrollLeft;
    const scrollWidth = timelineScrollEl.current.scrollWidth;
    const clientWidth = timelineScrollEl.current.clientWidth;

    // Calculate the distance to the end of the scroll
    const maxScrollLeft = scrollWidth - clientWidth;
    const remainingScroll = maxScrollLeft - scrollLeft;

    setScrollPosTimeline(remainingScroll);
  };

  // Scroll Into View Current Item
  const scrollToCurrentElement = () => {
    currentPlaylistItem.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
    // if (window !== undefined) {
    //   window.scrollTo(0, 0);
    // }
  };

  // Picking a session plays it: a different one once its stream loads, the
  // current one again from the start.
  const selectSession = (index: number) => {
    setIsBusinessCardPlaying(false);
    setWasBusinessCardShown(true);
    setCurrentSegment(null);
    if (index === currentSessionIndex) {
      setSkipToTime(0.0001);
      return;
    }
    requestPlayWhenReady();
    setCurrentSessionIndex(index);
    setCurrentPlaybackTime(0);
  };

  // Next skips what the timeline itself won't play: sessions with no poster
  // (upload incomplete) and, in highlights-only mode, sessions without any.
  const nextSessionIndex = currentPlaylist.findIndex(
    (item, index) =>
      index > currentSessionIndex &&
      Boolean(item.poster) &&
      (!isHighlightsOnlyMode || item.highlights.length > 0),
  );

  const timeLineHeights =
    "h-[70px] min-h-[70px] md:h-[100px] md:min-h-[90px] [@media(max-height:425px)]:h-[70px] [@media(max-height:425px)]:min-h-[70px]";
  // RENDER //////////////////////////////////////////////////////////////////////
  return (
    <div className={cn("w-full flex flex-col md:pb-2 gap-1", "bg-background")}>
      <div>
        <MetaAreaDiv pid={pid} meta={meta} widgetMode={widgetMode} />
      </div>

      <div
        className="flex-1 flex items-center"
        style={{
          borderBottom: "solid 1px #00000090",
        }}
      >
        <div
          ref={timelineScrollEl}
          onMouseEnter={() => {
            setRollOverTimeline(true);
          }}
          onMouseOut={() => {
            setRollOverTimeline(false);
          }}
          className={cn(
            "dark:bg-[#1e262fCD] bg-stone-200",
            "rounded-tr-sm relative rounded-br-sm w-full h-full  flex gap-3 p-2 relative overflow-x-hidden overflow-y-clip",
            timeLineHeights,
          )}
        >
          <div
            ref={anchor}
            key={-1}
            id="timeline__anchor"
            style={{
              display: "block",
              width: "1px",
              height: "100%",
              background: "transparent",
            }}
          ></div>
          <div className="-ml-3 -mr-2 flex items-center">
            <MdDragHandle className="-rotate-90 text-foreground" />
          </div>
          {currentPlaylist.map((timelineItem, timelineIndex: number) => {
            if (
              isHighlightsOnlyMode &&
              !currentPlaylist[timelineIndex].highlights.length
            )
              return null;
            return (
              <TimeLineTile
                ref={
                  timelineIndex === currentSessionIndex
                    ? currentPlaylistItem
                    : null
                }
                item={timelineItem}
                key={`${timelineItem.id}_${timelineIndex}`}
                index={timelineIndex}
                currentSessionIndex={currentSessionIndex}
                onClick={() => selectSession(timelineIndex)}
              />
            );
          })}
          {/* <button
                className="playlistItemBusinessCard"
                // data-tip={"► Business Card"}
                // data-for="TimelineTooltip"
                onClick={() =>
                  showBusinessCard(
                    setIsBusinessCardPlaying,
                    setVideoJsOptions,
                    currentProjectInfo
                  )
                }
              >
                <div
                  className={cn(
                    isBusinessCardPlaying
                      ? "bg-green-500 hover:bg-green-600"
                      : "bg-blue-500 hover:bg-blue-600",
                    "relative flex items-center justify-center rounded-sm aspect-video h-full bg-cover border-2 border-transparent"
                  )}
                  key={9999}
                >
                  <div>{isBusinessCardPlaying ? "hide" : "show"}</div>
                </div>
              </button> */}
          <div className="-ml-3 -mr-2 flex items-center">
            <RxDragHandleHorizontal className="-rotate-90 mx-4 text-black dark:text-zinc-600" />
          </div>{" "}
        </div>

        {currentPlaylist?.length > 1 && (
          <button
            type="button"
            onClick={() => selectSession(nextSessionIndex)}
            disabled={nextSessionIndex === -1}
            title={nextSessionIndex === -1 ? "Last session" : "Next session"}
            aria-label="Next session"
            className={cn(
              "w-[67px] self-stretch",
              "text-center",
              "font-Cabin",
              "dark:text-stone-200",
              "text-stone-800",
              "items-center",
              "justify-center",
              "hidden md:flex",
              "flex-col",
              "rounded-sm",
              "whitespace-nowrap",
              "relative",
              "cursor-pointer hover:text-accent hover:bg-accent/10 transition-colors",
              "disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-inherit",
            )}
          >
            <div
              id="Shadow_Over_Timeline"
              style={{
                opacity: !scrollPosTimeline
                  ? 1
                  : scrollPosTimeline >= 120
                  ? 0.8
                  : scrollPosTimeline >= 100
                  ? 0.75
                  : scrollPosTimeline >= 80
                  ? 0.5
                  : scrollPosTimeline >= 40
                  ? 0.25
                  : 0,
              }}
              className="absolute -left-12 md:-top-2 top-0 md:-bottom-2 bottom-0 w-12 z-10 bg-gradient-to-l fadeEdges dark:from-black from-stone-200 to-transparent pointer-events-none"
            />
            <span>{currentSessionIndex + 1}</span>
            <span>
              <RxDragHandleHorizontal className="opacity-30" />
            </span>
            <span>{currentPlaylist?.length}</span>
            <ChevronRight size={16} className="mt-0.5" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}

export default TimeLine;
